import _ from "underscore";
import DriveView from "../../DriveView";
import Api from "../../services/api";
import toastr from "../../utils/toastr";
import Spinner from "../../utils/spinner";
import { NetworkStates, PageLevel } from "../../store/mobile/types";

import pageHTML from "./timezoneChange.tmpl";
import { ListView, generateChecklist } from "../framework/checklist";
import { createElement, hide, show } from "../../utils/vanilla";
import { handleApi } from "../../utils/error";
import { ITimeZoneInfoDetails } from "../../utils/date";
import { getUser, setUser } from "./helpers";

interface SelectableTimezone {
    id: string;
    title: string;
    selected: boolean;
}

export default class ChangeTimezoneView extends DriveView<Backbone.Model> {

    protected strings = [
        "Timezone changed",
        "You cannot change your timezone while offline."
    ];
    private readonly api: Api;
    private readonly currentUser: DeepReadonlyObject<IUser>;
    private readonly template = _.template(pageHTML);
    private readonly mostUsedUSATimezones = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu"];
    private readonly mostUsedCanadaTimezones = ["America/Edmonton", "America/Halifax", "Pacific/St_Johns", "America/Toronto", "America/Vancouver", "America/Winnipeg"];
    private readonly mostPopularAmericaTimezones = this.mostUsedUSATimezones.concat(this.mostUsedCanadaTimezones);
    private readonly timezoneSpinner = new Spinner();
    private readonly waiting = new Spinner();

    private readonly keyupTimeoutMs = 500;
    private allTimezones: ITimeZoneInfoDetails[];
    private selectableTimezonesList: SelectableTimezone[];
    private timezonesHtml: HTMLDivElement;
    private timezoneList: ListView | undefined;
    private timezoneSearch: HTMLInputElement;

    constructor(options) {
        super(options, {
            title: "Timezone",
            activeSection: "settings",
            pageLevel: PageLevel.TWO
        });
        const canEditSettings = this.app.securityService.can("editUser");
        if (!canEditSettings) {
            // silently redirect to settings
            this.app.router.navigate("settings/main", true);
        }

        const user = this.app.userService.getCurrentUser();
        if (!user) {
            this.createErrorDialog("Failed to load current user");
            this.app.router.navigate("settings/main", true);
        }
        this.api = this.app.api;
        this.currentUser = user as DeepReadonlyObject<IUser>;

        this.api.call("GetTimeZones", {}, timezones => {
            this.allTimezones = timezones;
            this.timezonesHtml = createElement("div", undefined, "timezones-container");
            this.updateTimezoneList(this.getTopTimezones());
            this.render();
        });
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #submitChange": this.submitChange,
            "keyup #driver-timezone-search-input": this.searchTimezones
        } as Backbone.EventsHash;
    }

    render() {
        void this.display(this.template()).then(() => {
            const spinner = document.getElementById("timezones-search-spinner");
            if (spinner) {
                show(spinner);
                this.timezoneSpinner?.spin(spinner);
            }
            this.timezoneSearch = document.getElementById("driver-timezone-search-input") as HTMLInputElement;
            let listElement = (document.getElementById("timezones-list") as HTMLElement);
            if (listElement && this.timezonesHtml) {
                listElement.appendChild(this.timezonesHtml);
                spinner && (hide(spinner) && this.timezoneSpinner?.stop());
            }
        });
        return this;
    }

    onDone() {
        this.stopProgress();
        toastr.success(this.getString(this.strings[0]));
        this.app.router.navigate("settings/main", true);
    }

    createErrorDialog(errorMessage) {
        toastr.error(
            this.getString(errorMessage),
            undefined,
            undefined,
            {
                isUserNightMode: this.app.store.getState().data.nightMode,
                toastId: "timezone_change_error",
                origin: "settings_timezone_changene"
            }
        );
    }

    private readonly getTopTimezones = () => {
        return this.allTimezones.filter(tz => this.mostPopularAmericaTimezones.some(usaTz => usaTz === tz.id)).sort((a, b) => a.id.localeCompare(b.id));
    };

    private readonly updateTimezoneList: any = (timezones: ITimeZoneInfoDetails[]) => {
        this.selectableTimezonesList = [];
        const currentUserTimezoneId = this.currentUser?.timeZoneId;
        if (timezones.some(timezone => timezone.id !== currentUserTimezoneId)) {
            // try add current timezone to the top of the list
            const userTZ = this.allTimezones.find(tz => tz.id === currentUserTimezoneId);
            userTZ && timezones.unshift(userTZ);
        }
        this.selectableTimezonesList = timezones.map(timezone => {
            return {
                id: timezone.id,
                title: timezone.id,
                selected: currentUserTimezoneId === timezone.id
            };
        });
        this.timezoneList && this.timezoneList.remove();
        this.timezoneList = this.getTimezoneListView(this.timezonesHtml, this.selectableTimezonesList);
    };

    private getTimezoneListView(rulesetHtml: HTMLDivElement, selectableTimeZones: SelectableTimezone[]) {
        return generateChecklist({
            items: selectableTimeZones,
            isCheckable: true,
            labelledBy: "timezone-change-title",
            beforeSelectCallback: item => {
                if (item.get("selected") || item.get("disabled")) {
                    return false;
                } else {
                    let currentItem = this.timezoneList ? this.timezoneList.collection.findWhere({ "selected": true }) : undefined;
                    currentItem && currentItem.set({ "selected": false });
                    return true;
                }
            },
            selectCallback: () => { },
            context: this,
            translator: this.translator,
            el: rulesetHtml,
            noTranslate: false
        });
    }

    private startProgress() {
        const spinner = document.getElementById("timezones-search-spinner");
        spinner && (show(spinner) && this.timezoneSpinner?.spin(spinner));
        let submitButton = <HTMLButtonElement> this.el.querySelector("#submitChange");
        submitButton && submitButton.setAttribute("disabled", "disabled");
        submitButton && this.waiting.spin(submitButton);
    }

    private stopProgress() {
        const spinner = document.getElementById("timezones-search-spinner");
        spinner && (hide(spinner) && this.timezoneSpinner?.stop());
        let submitButton = <HTMLButtonElement> this.el.querySelector("#submitChange");
        submitButton && submitButton.removeAttribute("disabled");
        submitButton && this.waiting.stop();
    }

    private searchTimezones() {
        const submitChangeButton = document.getElementById("submitChange") as HTMLElement;
        const filterTZ = (tzName?: string) => {
            submitChangeButton && hide(submitChangeButton);
            const tzToShow = tzName ? this.allTimezones.filter(tz => tz.id.toLowerCase().includes(tzName.toLowerCase())) : this.getTopTimezones();
            this.updateTimezoneList(tzToShow);
            submitChangeButton && show(submitChangeButton);
        };

        const frequentlyUsedTitle = document.getElementById("frequently-used--timezones-title") as HTMLElement;
        if (this.timezoneSearch.value) {
            frequentlyUsedTitle && hide(frequentlyUsedTitle);
            setTimeout(() => {
                filterTZ(this.timezoneSearch.value);
            }, this.keyupTimeoutMs);
        } else {
            filterTZ();
            frequentlyUsedTitle && show(frequentlyUsedTitle);
        }
    }

    private submitChange() {
        this.startProgress();
        const selectedTimeZoneId = this.timezoneList ? this.timezoneList.collection.findWhere({ selected: true }).get("id") : undefined;

        if (selectedTimeZoneId === undefined) {
            this.createErrorDialog("Something went wrong. Invalid timezoneid selected.");
            this.stopProgress();
            return;
        }

        if (this.app.store.getState().mobile.online !== NetworkStates.ONLINE) {
            this.createErrorDialog(this.getString(this.strings[1]));
            this.stopProgress();
            return;
        }

        getUser(this.api, this.currentUser.id).then(user => {
            const updatedUser = {
                ...user,
                timeZoneId: selectedTimeZoneId
            };
            setUser(this.api, updatedUser).then(() => {
                this.app.userService.setUser(updatedUser);
                this.onDone();
            }).catch(error => {
                this.stopProgress();
                if (error) {
                    this.createErrorDialog(error.message);
                    handleApi(error);
                }
            });
        }).catch(error => {
            this.stopProgress();
            if (error) {
                this.createErrorDialog("We could not retrieve user information, please try again.");
                handleApi(error);
            }
        });
    }
}